(() => {
  const ROOT_SELECTOR = 'product-info.mm-product-experience';
  const ATELIER_TIME_ZONE = 'Europe/Istanbul';

  const getAtelierWallClock = (now = new Date()) => {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: ATELIER_TIME_ZONE,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hourCycle: 'h23',
      })
        .formatToParts(now)
        .reduce((values, part) => {
          if (part.type !== 'literal') values[part.type] = Number(part.value);
          return values;
        }, {});

      return new Date(
        Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second)
      );
    } catch (_error) {
      return new Date(now.getTime() + 3 * 60 * 60 * 1000);
    }
  };

  const getDateKey = (date) =>
    `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
      date.getUTCDate()
    ).padStart(2, '0')}`;

  const parseNonWorkingDates = (value) =>
    new Set(
      String(value || '')
        .split(/[\s,;]+/)
        .map((date) => date.trim())
        .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
    );

  const isWorkingDay = (date, nonWorkingDates = new Set()) => {
    const day = date.getUTCDay();
    return day !== 0 && day !== 6 && !nonWorkingDates.has(getDateKey(date));
  };

  const getNextOrderCutoff = (wallClock, cutoffHour, nonWorkingDates = new Set()) => {
    const cutoff = new Date(wallClock);
    cutoff.setUTCHours(cutoffHour, 0, 0, 0);

    if (!isWorkingDay(wallClock, nonWorkingDates) || wallClock >= cutoff) {
      do {
        cutoff.setUTCDate(cutoff.getUTCDate() + 1);
      } while (!isWorkingDay(cutoff, nonWorkingDates));
      cutoff.setUTCHours(cutoffHour, 0, 0, 0);
    }

    return cutoff;
  };

  const addWorkingDays = (startDate, numberOfDays, nonWorkingDates = new Set()) => {
    const result = new Date(startDate);
    let remaining = Math.max(0, Number(numberOfDays) || 0);

    while (remaining > 0) {
      result.setUTCDate(result.getUTCDate() + 1);
      if (isWorkingDay(result, nonWorkingDates)) remaining -= 1;
    }

    return result;
  };

  const formatDeliveryDate = (date, atelierYear) => {
    const locale = document.documentElement.lang || navigator.language || 'en-US';
    const options = {
      timeZone: 'UTC',
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    };
    if (date.getUTCFullYear() !== atelierYear) options.year = 'numeric';

    try {
      return new Intl.DateTimeFormat(locale, options).format(date);
    } catch (_error) {
      return new Intl.DateTimeFormat('en-US', options).format(date);
    }
  };

  const initOrderCountdown = (root) => {
    const card = root.querySelector('[data-mm-order-countdown]');
    const timer = card?.querySelector('[data-mm-countdown]');
    const daysWrap = timer?.querySelector('[data-mm-countdown-days-wrap]');
    const daysOutput = timer?.querySelector('[data-mm-countdown-days]');
    const hoursOutput = timer?.querySelector('[data-mm-countdown-hours]');
    const minutesOutput = timer?.querySelector('[data-mm-countdown-minutes]');
    const secondsOutput = timer?.querySelector('[data-mm-countdown-seconds]');
    const arrivalOutput = card?.querySelector('[data-mm-arrival-date]');
    if (!card || !timer || !hoursOutput || !minutesOutput || !secondsOutput || !arrivalOutput) return;

    const cutoffHour = Math.min(20, Math.max(12, Number(card.dataset.cutoffHour) || 17));
    const processingDays = Math.min(10, Math.max(1, Number(card.dataset.processingDays) || 4));
    const transitDays = Math.min(10, Math.max(1, Number(card.dataset.transitDays) || 3));
    const nonWorkingDates = parseNonWorkingDates(card.dataset.nonWorkingDates);
    let intervalId = null;
    let lastCutoffKey = '';

    const update = () => {
      if (!root.isConnected) {
        if (intervalId) window.clearInterval(intervalId);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        return;
      }

      const wallClock = getAtelierWallClock();
      const cutoff = getNextOrderCutoff(wallClock, cutoffHour, nonWorkingDates);
      const remainingSeconds = Math.max(0, Math.floor((cutoff.getTime() - wallClock.getTime()) / 1000));
      const days = Math.floor(remainingSeconds / 86400);
      const hours = Math.floor((remainingSeconds % 86400) / 3600);
      const minutes = Math.floor((remainingSeconds % 3600) / 60);
      const seconds = remainingSeconds % 60;

      if (daysOutput && daysWrap) {
        daysOutput.textContent = String(days);
        daysWrap.hidden = days === 0;
      }
      hoursOutput.textContent = String(hours).padStart(2, '0');
      minutesOutput.textContent = String(minutes).padStart(2, '0');
      secondsOutput.textContent = String(seconds).padStart(2, '0');
      timer.setAttribute(
        'aria-label',
        `${days ? `${days} days, ` : ''}${hours} hours, ${minutes} minutes, and ${seconds} seconds remaining in the current atelier order window`
      );

      const cutoffKey = cutoff.toISOString();
      if (cutoffKey !== lastCutoffKey) {
        const dispatchDate = addWorkingDays(cutoff, processingDays, nonWorkingDates);
        const deliveryDate = addWorkingDays(dispatchDate, transitDays, nonWorkingDates);
        arrivalOutput.dateTime = getDateKey(deliveryDate);
        arrivalOutput.textContent = formatDeliveryDate(deliveryDate, wallClock.getUTCFullYear());
        lastCutoffKey = cutoffKey;
      }

      card.classList.add('is-ready');
    };

    const startTimer = () => {
      update();
      if (!intervalId) intervalId = window.setInterval(update, 1000);
    };

    const stopTimer = () => {
      if (!intervalId) return;
      window.clearInterval(intervalId);
      intervalId = null;
    };

    function handleVisibilityChange() {
      if (document.hidden || card.hidden) {
        stopTimer();
      } else {
        startTimer();
      }
    }

    const setAvailability = (available) => {
      card.hidden = !available;
      if (available && !document.hidden) {
        startTimer();
      } else {
        stopTimer();
      }
    };

    setAvailability(card.dataset.productAvailable !== 'false');
    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS === 'object' &&
      PUB_SUB_EVENTS.variantChange
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data?.sectionId) !== String(root.dataset.section) || !data?.variant) return;
        setAvailability(Boolean(data.variant.available));
      });
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      card.classList.add('is-in-view');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          card.classList.add('is-in-view');
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.25 }
    );
    observer.observe(card);
  };

  const initHeaderOffset = (root) => {
    const header = document.querySelector('.shopify-section-header');
    if (!header) return;

    const update = () => root.style.setProperty('--mm-header-h', `${header.offsetHeight || 80}px`);
    update();

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(update);
      observer.observe(header);
    } else {
      window.addEventListener('resize', update, { passive: true });
    }
  };

  const initGalleryProgress = (root) => {
    const counter = root.querySelector('media-gallery .slider-counter');
    const current = counter?.querySelector('.slider-counter--current');
    const total = Number(counter?.querySelector('.slider-counter--total')?.textContent);
    if (!counter || !current || !total) return;

    const update = () => {
      const active = Math.max(1, Number(current.textContent) || 1);
      counter.style.setProperty('--mm-progress', `${Math.min(100, (active / total) * 100)}%`);
    };

    update();
    const observer = new MutationObserver(update);
    observer.observe(current, { childList: true, characterData: true, subtree: true });
  };

  const initReveal = (root) => {
    const items = [...root.querySelectorAll('.mm-reveal')];
    if (!items.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-in-view'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in-view');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.14 }
    );
    items.forEach((item) => observer.observe(item));
  };

  const initStickyBuy = (root) => {
    const sticky = root.querySelector('[data-mm-sticky-buy]');
    const stickyButton = sticky?.querySelector('[data-mm-sticky-button]');
    const stickyPrice = sticky?.querySelector('[data-mm-sticky-price]');
    const mainButton = root.querySelector('.product-form__submit');
    const productSection = root.closest('.shopify-section');
    if (!sticky || !stickyButton || !mainButton || !productSection) return;

    let mainButtonOffscreen = false;
    let sectionVisible = true;

    const sync = () => {
      const currentMainButton = root.querySelector('.product-form__submit');
      const currentPrice =
        root.querySelector('[id^="price-"] .price-item--sale') ||
        root.querySelector('[id^="price-"] .price-item--regular');

      if (stickyPrice && currentPrice) stickyPrice.textContent = currentPrice.textContent.trim();
      if (currentMainButton) {
        stickyButton.disabled = currentMainButton.disabled;
        stickyButton.textContent =
          currentMainButton.querySelector('span')?.textContent.trim() || currentMainButton.textContent.trim();
      }

      sticky.classList.toggle(
        'is-visible',
        mainButtonOffscreen && sectionVisible && !stickyButton.disabled
      );
    };

    const buttonObserver = new IntersectionObserver(
      ([entry]) => {
        // This used to also require `entry.boundingClientRect.top < 0`,
        // so the bar only appeared once the shopper had scrolled PAST
        // the button. On a phone the button starts below the fold, which
        // left the page with no call to action in view at all until they
        // scrolled a long way down. The bar now shows whenever the real
        // button is off screen in either direction, and steps aside when
        // it comes into view so the two never compete.
        mainButtonOffscreen = !entry.isIntersecting;
        sync();
      },
      { threshold: 0 }
    );
    buttonObserver.observe(mainButton);

    const sectionObserver = new IntersectionObserver(
      ([entry]) => {
        sectionVisible = entry.isIntersecting;
        sync();
      },
      { threshold: 0 }
    );
    sectionObserver.observe(productSection);

    stickyButton.addEventListener('click', () => {
      root.querySelector('.product-form__submit:not([disabled])')?.click();
    });

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS === 'object' &&
      PUB_SUB_EVENTS.variantChange
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data?.sectionId) !== String(root.dataset.section)) return;
        window.requestAnimationFrame(sync);
      });
    }

    sync();
  };

  // Money on this store renders as "$123.48 USD". Shopify's money filters
  // separate the currency code and group digits with a non-breaking space,
  // so this strips \s (which matches U+00A0 as well as ordinary spaces)
  // rather than a literal space. Kept as an ASCII escape deliberately: the
  // previous literal U+00A0 here was invisible in a diff and easy to replace
  // with a plain space by accident, which silently broke parsing. Returns
  // null rather than NaN so callers can simply skip rendering when a
  // currency or locale format does not parse, instead of printing nonsense.
  const parseMoney = (text) => {
    const match = String(text || '')
      .replace(/[\s,]/g, '')
      .match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : null;
  };

  const currencyPrefix = (text) => (String(text || '').match(/^[^\d]*/) || [''])[0].trim();

  const readCurrentPriceEl = (root) =>
    root.querySelector('[id^="price-"] .price-item--sale') ||
    root.querySelector('[id^="price-"] .price-item--regular');

  // The shop's review record renders in its own section about 2,600px
  // below the buy button, which is far too late to help anyone decide.
  // This mirrors the headline figures directly under the price. They are
  // read out of that section rather than hardcoded, so the two can never
  // drift apart, and nothing renders at all if the section is absent.
  const initTrustLine = (root) => {
    const price = root.querySelector('.mm-product-price');
    if (!price || root.querySelector('.mm-trust-line')) return;

    const scoreText = document.querySelector('.mm-rv__figure')?.textContent.trim();
    const countText = document.querySelector('.mm-rv__console-breakdown')?.textContent.trim();
    if (!scoreText || !countText) return;

    const reviewsSection = document
      .querySelector('.mm-rv__console-breakdown')
      .closest('.shopify-section');

    const line = document.createElement(reviewsSection?.id ? 'a' : 'span');
    if (reviewsSection?.id) line.href = `#${reviewsSection.id}`;
    line.className = 'mm-trust-line';

    const stars = document.createElement('span');
    stars.className = 'mm-trust-line__stars';
    stars.setAttribute('aria-hidden', 'true');
    stars.textContent = '★★★★★';

    const score = document.createElement('span');
    score.className = 'mm-trust-line__score';
    score.textContent = scoreText;

    const count = document.createElement('span');
    count.className = 'mm-trust-line__count';
    count.textContent = countText;

    line.append(stars, score, count);
    price.after(line);
  };

  // The trust line above carries the numbers but no voice, and the section
  // that carries the voice starts about three screens below the buy button.
  // This lifts a single quote up next to the assurance strip, where the
  // decision is actually being made.
  //
  // The quote is read out of the reviews section's own marquee rather than
  // chosen here. That marquee is already the merchant's pick: the section
  // pins it via its marquee_handle setting, and otherwise selects the
  // shortest five-star review itself. Reading it means there is one source
  // of truth and no second selection rule to keep in sync. Nothing renders
  // if the section is absent or the marquee is switched off, apart from the
  // card fallback below.
  const initReviewQuote = (root) => {
    const assurance = root.querySelector('.mm-purchase-assurance');
    if (!assurance || root.querySelector('.mm-review-quote')) return;

    let text = document.querySelector('.mm-rv__marquee-quote')?.textContent.trim();
    let name = document.querySelector('.mm-rv__marquee-name')?.textContent.trim();

    // With the marquee off, fall back to the shortest five-star card quote.
    // Length is capped deliberately: the longer reviews on this shop tend to
    // carry caveats, and a five-star rating alone does not guarantee a
    // wholly positive read.
    if (!text) {
      const best = [...document.querySelectorAll('.mm-rv__card')]
        .filter((card) =>
          /^5 /.test(card.querySelector('.mm-rv__stars')?.getAttribute('aria-label') || '')
        )
        .map((card) => ({
          text: card.querySelector('.mm-rv__quote')?.textContent.trim() || '',
          name: card.querySelector('.mm-rv__name')?.textContent.trim() || ''
        }))
        .filter((c) => c.text.length >= 40 && c.text.length <= 110)
        .sort((a, b) => a.text.length - b.text.length)[0];
      if (!best) return;
      text = best.text;
      name = best.name;
    }
    if (!text) return;

    const reviewsSection = document
      .querySelector('.mm-rv__console-breakdown, .mm-rv__marquee-quote, .mm-rv__card')
      ?.closest('.shopify-section');

    const attribution = name ? `${name}, verified on Etsy` : 'Verified on Etsy';

    const wrap = document.createElement(reviewsSection?.id ? 'a' : 'div');
    if (reviewsSection?.id) wrap.href = `#${reviewsSection.id}`;
    wrap.className = 'mm-review-quote';
    wrap.setAttribute(
      'aria-label',
      `Five star review. “${text}” ${attribution}. Read all reviews.`
    );

    const stars = document.createElement('span');
    stars.className = 'mm-review-quote__stars';
    stars.setAttribute('aria-hidden', 'true');
    stars.textContent = '★★★★★';

    const quote = document.createElement('span');
    quote.className = 'mm-review-quote__text';
    quote.setAttribute('aria-hidden', 'true');
    quote.textContent = `“${text}”`;

    const by = document.createElement('span');
    by.className = 'mm-review-quote__by';
    by.setAttribute('aria-hidden', 'true');
    by.textContent = attribution;

    wrap.append(stars, quote, by);
    assurance.after(wrap);
  };

  // "Complimentary shipping over $200" is already stated in the assurance
  // strip, so the threshold is read from there rather than hardcoded:
  // one source of truth, and the nudge cannot contradict the actual
  // policy if it changes. Hidden when the product already qualifies, or
  // when either figure fails to parse.
  const initShippingNudge = (root) => {
    const assurance = root.querySelector('.mm-purchase-assurance');
    const buyButtons = root.querySelector('.product-form__buttons');
    if (!assurance || !buyButtons || root.querySelector('.mm-shipping-nudge')) return;

    const shippingItem = [...assurance.children].find((item) =>
      /complimentary shipping/i.test(item.textContent)
    );
    const threshold = parseMoney(shippingItem?.textContent);
    if (!threshold) return;

    const nudge = document.createElement('p');
    nudge.className = 'mm-shipping-nudge';
    buyButtons.before(nudge);

    const update = () => {
      const priceEl = readCurrentPriceEl(root);
      const price = parseMoney(priceEl?.textContent);
      const gap = price === null ? null : threshold - price;

      if (gap === null || gap <= 0) {
        nudge.hidden = true;
        return;
      }

      const prefix = currencyPrefix(priceEl?.textContent);
      nudge.hidden = false;
      nudge.textContent = `Add ${prefix}${gap.toFixed(2)} more for complimentary shipping`;
    };

    update();

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS === 'object' &&
      PUB_SUB_EVENTS.variantChange
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data?.sectionId) !== String(root.dataset.section)) return;
        window.requestAnimationFrame(update);
      });
    }
  };

  // The breadcrumb is authored inside the product info card, which stacks
  // below the gallery on a phone. That buried the only route back to the
  // collection a full screen down. It has to cross a container boundary
  // to sit above the gallery, which CSS alone cannot do, so it is moved
  // here and put back when the viewport grows. The node is moved, never
  // cloned, so there is still exactly one breadcrumb nav in the DOM. Its
  // sibling <script> (BreadcrumbList JSON-LD) and <style> stay where they
  // are, which is fine: neither depends on document position.
  const initMobileBreadcrumb = (root) => {
    const breadcrumb = root.querySelector('.mm-shop-breadcrumbs');
    const media = root.querySelector('.product__media-wrapper');
    const product = media?.parentElement;
    if (!breadcrumb || !media || !product || breadcrumb.contains(media)) return;

    const anchor = document.createComment('mm-breadcrumb-origin');
    breadcrumb.before(anchor);

    const query = window.matchMedia('(max-width: 749px)');

    const place = () => {
      if (query.matches) {
        if (breadcrumb.parentElement !== product) product.insertBefore(breadcrumb, media);
      } else if (breadcrumb.parentElement === product && anchor.parentElement) {
        anchor.after(breadcrumb);
      }
    };

    place();

    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', place);
    } else {
      query.addListener(place);
    }
  };

  const initRoot = (root) => {
    if (root.dataset.mmProductInitialized === 'true') return;
    root.dataset.mmProductInitialized = 'true';
    document.body.classList.add('template-tet');

    initHeaderOffset(root);
    initOrderCountdown(root);
    initGalleryProgress(root);
    initReveal(root);
    initStickyBuy(root);
    initMobileBreadcrumb(root);
    initTrustLine(root);
    initReviewQuote(root);
    initShippingNudge(root);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();